// EditMilestoneDrawer.js
import React, { useState, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  TextField,
  Button,
  Autocomplete,
} from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useFormik } from "formik";
import * as Yup from "yup";
import { UserCreate, userUpdate } from "../../../api/casp/user";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Action, Role } from "../../../constants/Common";
import { withTranslation } from "react-i18next";
// import { Clear } from "@material-ui/icons";
import { getAllProject } from "../../../api/casp/project";
import SearchDropDownListPaginationComponent from "../../../Components/casp/common/SearchDropDownPaginationList";
import { Clear } from "@mui/icons-material";

const AddUserDrawer = ({ isOpen, handleClose, action, selectedUserRow, t }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [triggerData, setTriggerData] = useState(+new Date());
  const [userObject, setUserObject] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNo: "",
    gender: "",
    department: "",
    project: {},
    address: {
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
  });
  const { user } = useSelector((state) => ({
    user: state.Profile.user,
  }));

  useEffect(() => {
    if (selectedUserRow !== undefined && action === Action.EDIT) {
      formik.setValues({
        ...selectedUserRow,
        project: {
          id: selectedUserRow?.project?.id,
          label: selectedUserRow?.project?.name,
          key: `${selectedUserRow?.project?.id}|${selectedUserRow?.project?.name}`,
        },
        address: {
          city: selectedUserRow?.address?.city ?? "",
          state: selectedUserRow?.address?.state ?? "",
          country: selectedUserRow?.address?.country ?? "",
          zipCode: selectedUserRow?.address?.zipCode?.toString() ?? "",
          addressLine: selectedUserRow?.address?.addressLine ?? "",
        },
        department: selectedUserRow.department
          ? selectedUserRow.department
          : "",
      });
    }
  }, [selectedUserRow]);

  useEffect(() => {
    fetchProjectList();
  }, [triggerData]);

  const fetchProjectList = async () => {
    try {
      setIsLoading(true);
      // let params = {
      //   filterFields: ["status"],
      //   filterInputs: [ProjectStatuses.ONGOING]
      // }
      const res = (await getAllProject()).data.data;
      const resultData = res.data.map((res) => {
        return res.name;
      });

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const phoneRegExp = /^\+?[0-9]([0-9]|(-(?!-)))*[0-9]$/;
  const zipCodePattern = /^[0-9]*$/;
  const agendaValidation = Yup.object().shape({
    firstName: Yup.string()
      .strict(true)
      .trim(t("Must not include leading and trailing spaces"))
      .max(250, t("First name can not be greater than 250 characters"))
      .required(t("Please enter first name")),
    lastName: Yup.string()
      .strict(true)
      .trim(t("Must not include leading and trailing spaces"))
      .max(250, t("Last name can not be greater than 250 characters"))
      .required(t("Please enter last name")),
    email: Yup.string()
      .strict(true)
      .trim(t("Must not include leading and trailing spaces"))
      .email(t("Please enter valid email"))
      .required(t("Please enter email")),
    contactNo: Yup.string()
      .strict(true)
      .required(t("Please enter contact no"))
      .matches(phoneRegExp, t("Contact no is not valid")),
    gender: Yup.string().strict(true).required(t("Please enter gender")),
    department: Yup.string()
      .strict(true)
      .trim(t("Must not include leading and trailing spaces"))
      .optional(),
    project: Yup.object().required(t("Please select Project")),
    address: Yup.object()
      .shape({
        addressLine: Yup.string()
          .strict(true)
          .trim(t("Must not include leading and trailing spaces"))
          .optional(),
        city: Yup.string()
          .strict(true)
          .trim(t("Must not include leading and trailing spaces"))
          .optional(),
        state: Yup.string()
          .strict(true)
          .trim(t("Must not include leading and trailing spaces"))
          .optional(),
        zipCode: Yup.string()
          .strict(true)
          .trim(t("Must not include leading and trailing spaces"))
          .matches(zipCodePattern, t("Zip code is not valid"))
          .optional(),
        country: Yup.string()
          .strict(true)
          .trim(t("Must not include leading and trailing spaces"))
          .optional(),
      })
      .optional(),
  });

  const handleCreate = async () => {
    try {
      setIsLoading(true);
      const address = {
        ...(formik.values.address.addressLine && {
          addressLine: formik.values.address.addressLine,
        }),
        ...(formik.values.address.zipCode && {
          zipCode: formik.values.address.zipCode,
        }),
        ...(formik.values.address.state && {
          state: formik.values.address.state,
        }),
        ...(formik.values.address.city && {
          city: formik.values.address.city,
        }),
        ...(formik.values.address.country && {
          country: formik.values.address.country,
        }),
      };
      const response = await UserCreate({
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        email: formik.values.email,
        projectId: formik.values?.project?.id,
        ...(formik.values.department.trim() && {
          department: formik.values.department,
        }),
        address: address,
        contactNo: formik.values.contactNo,
        gender: formik.values.gender,
        companyId: user.company.id,
      });
      if (response.statusCode === 200) {
        toast.success(response.data.message);
        setIsLoading(false);
        handleCancel("success");
      } else {
        toast.error(response.data.message);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      toast.error(err.message);
    }
  };

  const handleEdit = async () => {
    try {
      setIsLoading(true);
      const address = {
        ...(formik.values.address.addressLine && {
          addressLine: formik.values.address.addressLine,
        }),
        ...(formik.values.address.zipCode && {
          zipCode: formik.values.address.zipCode,
        }),
        ...(formik.values.address.state && {
          state: formik.values.address.state,
        }),
        ...(formik.values.address.city && {
          city: formik.values.address.city,
        }),
        ...(formik.values.address.country && {
          country: formik.values.address.country,
        }),
      };
      const response = await userUpdate(selectedUserRow.id, {
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        ...(formik.values.department.trim() && {
          department: formik.values.department,
        }),
        projectId: formik.values?.project?.id,
        address: address,
        contactNo: formik.values.contactNo,
        gender: formik.values.gender,
        companyId: user.company.id,
        ...(selectedUserRow.addressId && {
          addressId: selectedUserRow.addressId,
        }),
      });
      if (response.statusCode === 200) {
        toast.success(response.data.message);
        setIsLoading(false);
        handleCancel("success");
      } else {
        toast.error(response.data.message);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      toast.error(err.message);
    }
  };

  const formik = useFormik({
    initialValues: {
      ...userObject,
    },
    validationSchema: agendaValidation,
    validateOnMount: true,
  });

  const handleCancel = (message, reason) => {
    if (reason === "backdropClick") {
      return;
    } else if (isOpen) {
      formik.resetForm();
      formik.setValues(formik.initialValues);
    }

    handleClose(message);
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleCancel}>
      <div style={{ width: "500px" }} className="editProjTypeDrawer">
        <List>
          <ListItem>
            <ListItemText>
              {action === Action.EDIT ? t("Edit User") : t("Add User")}
            </ListItemText>
            <ListItemIcon>
              <IconButton onClick={handleCancel}>
                <CancelOutlinedIcon />
              </IconButton>
            </ListItemIcon>
          </ListItem>
          <form>
            <ListItem>
              <TextField
                fullWidth
                id="outlined-basic"
                label={t("Enter First Name")}
                name="firstName"
                value={formik.values?.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isLoading}
                required
              />
            </ListItem>
            <div className="mx-3">
              {formik.touched.firstName && formik.errors.firstName && (
                <span className="text-danger">{formik.errors.firstName}</span>
              )}
            </div>
            <ListItem>
              <TextField
                fullWidth
                id="outlined-basic"
                label={t("Enter Last Name")}
                name="lastName"
                value={formik.values?.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isLoading}
                required
              />
            </ListItem>
            <div className="mx-3">
              {formik.touched.lastName && formik.errors.lastName && (
                <span className="text-danger">{formik.errors.lastName}</span>
              )}
            </div>
            <ListItem>
              <TextField
                fullWidth
                id="outlined-basic"
                label={t("Enter Email Address")}
                name="email"
                value={formik.values?.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isLoading || action === Action.EDIT}
                required
              />
            </ListItem>
            <div className="mx-3">
              {formik.touched.email && formik.errors.email && (
                <span className="text-danger">{formik.errors.email}</span>
              )}
            </div>
            <ListItem>
              <TextField
                fullWidth
                id="outlined-basic"
                label={t("Enter Contact No")}
                name="contactNo"
                value={formik.values?.contactNo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isLoading}
                required
              />
            </ListItem>
            <div className="mx-3">
              {formik.touched.contactNo && formik.errors.contactNo && (
                <span className="text-danger">{formik.errors.contactNo}</span>
              )}
            </div>
            <ListItem>
              <Autocomplete
                disabled={isLoading}
                id="combo-box-demo"
                sx={{ width: "100%" }}
                options={["Male", "Female"]}
                name="gender"
                size="small"
                value={formik.values?.gender}
                onChange={(e, val) => formik.setFieldValue("gender", val)}
                renderInput={(params) => (
                  <TextField {...params} label={t("Enter Gender")} required />
                )}
                clearIcon={formik.values?.gender ? <Clear /> : null}
                required
              />
            </ListItem>
            <div className="mx-3">
              {formik.touched.gender && formik.errors.gender && (
                <span className="text-danger">{formik.errors.gender}</span>
              )}
            </div>
            <ListItem>
              <TextField
                fullWidth
                id="outlined-basic"
                label={t("Enter Department")}
                name="department"
                value={formik.values?.department}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isLoading}
              />
            </ListItem>
            <div className="mx-3">
              {formik.touched.department && formik.errors.department && (
                <span className="text-danger">{formik.errors.department}</span>
              )}
            </div>
            <ListItem className="addUserDblCol d-flex align-items-start">
              <div className="singleCol addressLine">
                <TextField
                  date_width="true"
                  id="outlined-basic"
                  label={t("Enter Address Line")}
                  name="address.addressLine"
                  value={formik.values?.address?.addressLine}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isLoading}
                // style={{
                //   overflow: 'hidden',
                //   textOverflow: 'ellipsis',
                //   whiteSpace: 'nowrap'
                // }}
                />
                <div className="mx-2">
                  {formik?.touched?.address?.addressLine &&
                    formik?.errors?.address?.addressLine && (
                      <span className="text-danger">
                        {formik.errors.address.addressLine}
                      </span>
                    )}
                </div>
              </div>
              <div className="singleCol">
                <TextField
                  date_width="true"
                  id="outlined-basic"
                  label={t("Enter City")}
                  name="address.city"
                  value={formik.values?.address?.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isLoading}
                />
                <div className="mx-2">
                  {formik?.touched?.address?.city &&
                    formik?.errors?.address?.city && (
                      <span className="text-danger">
                        {formik.errors.address.city}
                      </span>
                    )}
                </div>
              </div>
              <div className="singleCol">
                <TextField
                  date_width="true"
                  id="outlined-basic"
                  label={t("Enter State")}
                  name="address.state"
                  value={formik.values?.address?.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isLoading}
                />
                <div className="mx-2">
                  {formik?.touched?.address?.state &&
                    formik?.errors?.address?.state && (
                      <span className="text-danger">
                        {formik.errors.address.state}
                      </span>
                    )}
                </div>
              </div>
            </ListItem>
            <ListItem className="addUserDblCol d-flex align-items-start">
              <div className="singleCol">
                <TextField
                  date_width="true"
                  id="outlined-basic"
                  label={t("Enter Zip Code")}
                  name="address.zipCode"
                  value={formik.values?.address?.zipCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isLoading}
                />
                <div className="mx-2">
                  {formik?.touched?.address?.zipCode &&
                    formik?.errors?.address?.zipCode && (
                      <span className="text-danger">
                        {formik.errors.address.zipCode}
                      </span>
                    )}
                </div>
              </div>
              <div className="singleCol">
                <TextField
                  date_width="true"
                  id="outlined-basic"
                  label={t("Enter Country")}
                  name="address.country"
                  value={formik.values?.address?.country}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isLoading}
                />
                <div className="mx-2">
                  {formik?.touched?.address?.country &&
                    formik?.errors?.address?.country && (
                      <span className="text-danger">
                        {formik.errors.address.country}
                      </span>
                    )}
                </div>
              </div>
            </ListItem>
            {/* {action === Action.EDIT ?
            <></> :  */}
            <ListItem>
              <SearchDropDownListPaginationComponent
                label={
                  <>
                    {t("Startup Name")} <span style={{ color: "red" }}>*</span>
                  </>
                }
                searchEnabled={false}
                disableClearable={!formik.values.project}
                listCall={getAllProject}
                defaultValue={
                  Object.keys(formik.values.project).length !== 0
                    ? formik.values.project
                    : undefined
                }
                size="small"
                sx={{ width: "100%" }}
                emitItem={(newValue) => {
                  formik.setFieldTouched("project", true, true);
                  formik.setFieldValue("project", newValue ? newValue : {});
                }}
                apiParams={{
                  filterFields: ['projectUserRole'],
                  filterInputs: [Role.PROJECT_MANAGER],
                  order: "updatedAt",
                  direction: "desc",
                }}
                onBlur={() => {
                  if (!formik.touched?.project) {
                    formik.setFieldTouched("project", true, true);
                  }
                }}
                required
              />
            </ListItem>
            {/* } */}
            <div className="mx-3">
              {formik.touched.project && formik.errors.project && (
                <span className="text-danger">{formik.errors.project}</span>
              )}
            </div>

            <ListItem className="justify-content-center mt-4">
              <Button
                onClick={action === Action.EDIT ? handleEdit : handleCreate}
                variant="contained"
                className="drawerCommonBtn greenBtn mx-2"
                type="submit"
                disabled={
                  isLoading || !formik.isValid || !formik.values?.project?.id
                }
              >
                {t("Submit")}
              </Button>
              <Button
                onClick={handleCancel}
                variant="contained"
                className="drawerCommonBtn redButton"
              >
                {t("Cancel")}
              </Button>
            </ListItem>
          </form>
        </List>
      </div>
    </Drawer>
  );
};

export default withTranslation()(AddUserDrawer);
